import type { Store, Middleware, PreloadedState } from 'redux';
import { createLogger as createReduxLogger } from 'redux-logger';
import type { Epic } from 'redux-observable';
import initiateStore from '@atlassian/jira-common-tangerine/src/state/initiate';
// eslint-disable-next-line jira/restricted/@atlassian+jira-errors-handling
import { errorHandlerReduxMiddleware } from '@atlassian/jira-errors-handling';
import { CachedStoreManager } from '@atlassian/jira-software-redux-cached-store-manager';
import { createJiraSoftwareStoreContext } from '@atlassian/jira-software-redux-hooks';
import { APP_ID } from '../model/constants';
import type { Action } from './actions';
import { SET_RICH_ACTIVE_CARD } from './actions/cards/rich-card';
import analyticsMiddleware from './middleware/analytics/middleware';
import resourcesCacheMiddleware from './middleware/resources-cache';
import rootReducer from './reducers/reducer';
import type { State } from './reducers/types';
import type { Dependencies } from './types';

export interface CreateStoreOptions {
	rootEpic?: Epic<Action, State>;
	dependencies?: Dependencies;
}
export default function createStore(
	preloadedState: PreloadedState<State> | null,
	{ rootEpic, dependencies }: CreateStoreOptions,
): Store<State> {
	const middlewares: Middleware[] = [
		analyticsMiddleware(dependencies),
		errorHandlerReduxMiddleware(APP_ID),
		resourcesCacheMiddleware,
	];

	if (process.env.NODE_ENV === 'development') {
		middlewares.push(
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			createReduxLogger({
				collapsed: true,
				duration: true,
				predicate: (_getState, action) => action.type !== SET_RICH_ACTIVE_CARD,
			}) as Middleware,
		);
	}

	return initiateStore<State, Action, Dependencies>({
		appName: 'backlog',
		rootReducer,
		initialState: preloadedState ?? undefined,
		rootEpic,
		middlewares,
		rootEpicDependencies: dependencies,
	});
}

export const manager = new CachedStoreManager({
	storeFactory: createStore,
	mergeCachedState: (cachedState, _preloadedState) => cachedState,
});

export function createStoreWithCache(
	boardId: string,
	initialState: PreloadedState<State>,
	options: CreateStoreOptions & { disableStoreCaches: boolean },
): Store<State> {
	return manager.createStore(boardId, initialState, options);
}

export const unmountCachedStore = () => {
	manager.unmount();
};

export const {
	Provider: BacklogReduxProvider,
	useStore: useBacklogStore,
	useSelector: useBacklogSelector,
	useDispatch: useBacklogDispatch,
	useActionCreator: useBacklogActionCreator,
} = createJiraSoftwareStoreContext<State, Action>();
